import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Sparkle, WarningCircle, SquaresFour, MagnifyingGlass, CaretDown, CaretUp } from '@phosphor-icons/react';
import MarkdownContent from './MarkdownContent';
import { transformItemReferences, ItemLink } from '../utils/itemLinks';
import ChatItemCard from './ChatItemCard';

/**
 * Individual chat message component with safe markdown rendering.
 * Extracted from ChatPanel for reuse in the full-page Chat view.
 */
export default function ChatMessage({ message, index, onItemClick }) {
  const [isItemsExpanded, setIsItemsExpanded] = useState(false);
  const [isItemsCollapsed, setIsItemsCollapsed] = useState(false);
  const isUser = message.role === 'user';
  const isError = message.role === 'error';
  const isHistory = String(message.id).startsWith('history-');
  const items = message.items || [];

  // Smart initial count: for 2-col grid, round down to even to fill last row
  const GRID_COLS = 2;
  const MAX_INITIAL = 10;
  const initialCount = items.length > MAX_INITIAL
    ? Math.floor(MAX_INITIAL / GRID_COLS) * GRID_COLS
    : items.length;

  // Transform item references in the content to clickable links
  const processedContent = isUser ? message.content : transformItemReferences(message.content, items);

  return (
    <motion.div
      className={`chat-message chat-message-${message.role}`}
      initial={isHistory ? false : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={isHistory ? { duration: 0 } : { delay: index * 0.05 }}
    >
      {!isUser && (
        <div className="chat-message-avatar">
          {isError ? <ErrorIcon /> : <SparkleIcon />}
        </div>
      )}

      <div className="chat-message-content">
        <MarkdownContent
          components={{
            a: (props) => <ItemLink {...props} onItemClick={onItemClick} items={items} />,
          }}
        >
          {processedContent}
        </MarkdownContent>

        {/* Referenced Items as Cards */}
        {items.length > 0 && (
          <div className="chat-items">
            <div
              className="chat-items-label"
              onClick={() => {
                const newCollapsed = !isItemsCollapsed;
                setIsItemsCollapsed(newCollapsed);
                if (newCollapsed) setIsItemsExpanded(false);
              }}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const newCollapsed = !isItemsCollapsed;
                  setIsItemsCollapsed(newCollapsed);
                  if (newCollapsed) setIsItemsExpanded(false);
                }
              }}
              aria-expanded={!isItemsCollapsed}
            >
              <span style={{
                display: 'inline-flex',
                transition: 'transform 150ms ease',
                transform: isItemsCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              }}>
                <ChevronDownIcon />
              </span>
              <ItemsIcon />
              <span>{items.length} item{items.length !== 1 ? 's' : ''} found</span>
            </div>
            {!isItemsCollapsed && (
              <>
                <div className="chat-items-grid">
                  {items.slice(0, initialCount).map((item, i) => (
                    <ChatItemCard
                      key={item.item_id || item.id || i}
                      item={item}
                      index={i}
                      onItemClick={onItemClick}
                    />
                  ))}
                  <AnimatePresence>
                    {isItemsExpanded && items.slice(initialCount).map((item, i) => (
                      <motion.div
                        key={item.item_id || item.id || `expanded-${i}`}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.2, delay: i * 0.02 }}
                      >
                        <ChatItemCard
                          item={item}
                          index={i + initialCount}
                          onItemClick={onItemClick}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
                {items.length > initialCount && (
                  <button
                    className="chat-items-toggle"
                    onClick={() => setIsItemsExpanded(!isItemsExpanded)}
                    aria-expanded={isItemsExpanded}
                    aria-label={isItemsExpanded ? 'Show fewer items' : `Show ${items.length - initialCount} more items`}
                  >
                    {isItemsExpanded ? (
                      <>
                        <ChevronUpIcon />
                        <span>Show less</span>
                      </>
                    ) : (
                      <>
                        <ChevronDownIcon />
                        <span>+{items.length - initialCount} more items</span>
                      </>
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {/* Tool calls indicator */}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <div className="chat-tool-indicator">
            <SearchIcon />
            <span>Searched your collection</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Utility function for relative time
export function formatRelativeTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// Icons
export function SparkleIcon() {
  return <Sparkle size={16} />;
}

function ErrorIcon() {
  return <WarningCircle size={16} />;
}

function ItemsIcon() {
  return <SquaresFour size={14} />;
}

function SearchIcon() {
  return <MagnifyingGlass size={12} />;
}

function ChevronDownIcon() {
  return <CaretDown size={12} />;
}

function ChevronUpIcon() {
  return <CaretUp size={12} />;
}
